import { buildContextoCobro, getCobroReal } from "./cobro/getCobroFactura";
import { client } from "./supabase";

// ─── Queries ──────────────────────────────────────────────

export const getMovimientosBancarios = async (pais, filters = {}) => {
  let query = client
    .from("movimientos_bancarios")
    // B3 Fase B paso 2: el vínculo banco↔factura SII se trae SOLO por el puente
    // `movimiento_bancario_facturas` (factura_tipo + factura_id). Ya NO se embeben las facturas por
    // la FK directa (`factura_sii_emitida_id`/`_recibida_id`, en retiro) — los lectores resuelven
    // folio/razón/tipo_dte del puente vía sus mapas por id (regla 10.192/10.194).
    .select(
      `*,
      facturas_vinculadas:movimiento_bancario_facturas(factura_tipo, factura_id),
      factura_externa:facturas_externas(numero_documento, archivo_url),
      movimiento_caja:movimientos_caja!movimiento_bancario_id(categoria_pago, entidad_nombre)`
    )
    .limit(5000)
    .eq("pais", pais)
    .order("fecha", { ascending: false });

  if (filters.cuenta_bancaria_id) query = query.eq("cuenta_bancaria_id", filters.cuenta_bancaria_id);
  if (filters.estado) query = query.eq("estado", filters.estado);
  if (filters.tipo) query = query.eq("tipo", filters.tipo);
  if (filters.fechaDesde) query = query.gte("fecha", filters.fechaDesde);
  if (filters.fechaHasta) query = query.lte("fecha", filters.fechaHasta);
  if (filters.moneda) query = query.eq("moneda", filters.moneda);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getMovimientosBancariosPendientes = async (pais) => getMovimientosBancarios(pais, { estado: "pendiente" });

// Devuelve sets de IDs de facturas que NO deberían ofrecerse en el modal de vinculación
// ni proyectarse como pendientes en los paneles "Por cobrar" / "Por pagar":
//   1. SII EMITIDAS: cobradas según `getCobroReal` — la lógica ÚNICA de cobro (MBF → factoring
//      liquidada → cuotas). B2/B3 / regla 10.192: el estado de cobro NO se lee de la FK directa cruda,
//      se resuelve por el chokepoint `getCobroReal`. Esto además saca de "Por cobrar" las emitidas de
//      factoring liquidada que las Fuentes viejas no veían (0 filas MBF por 10.193).
//   2. SII RECIBIDAS / BOLETAS de honorarios: pagadas por el puente MBF (Σ mbf.monto ≥ total) o por
//      plan de cuotas todas pagadas. Ya NO se lee la FK directa `factura_sii_recibida_id`: el puente
//      es superset en el eje pago (0 recibidas-solo-FK, historial 9.66) → eje pago listo para B3.
//   3. Facturas EXTERNAS: tienen movimiento_bancario_id directo en la propia fila.
export const getFacturasYaVinculadas = async (pais) => {
  const [movsRes, cobrosRes, pagosRes, mbfRes, externasPagadasRes, facEmitidasRes, opFactRes, opFactFacRes] =
    await Promise.all([
      // Movimientos del país: SOLO id + fecha. El resolver los usa únicamente para datar la vía MBF;
      // B3 Fase A retiró el fallback por FK directa (`factura_sii_emitida_id`), así que ya no se lee
      // ninguna FK directa cruda acá (regla 10.192).
      client.from("movimientos_bancarios").select("id, fecha").eq("pais", pais).range(0, 49999),
      client
        .from("plan_cobros_facturas")
        .select("factura_sii_emitida_id, estado, fecha_cobro_real, fecha_vencimiento")
        .eq("pais", pais)
        .range(0, 49999),
      client.from("plan_pagos_facturas").select("factura_sii_recibida_id, estado").eq("pais", pais).range(0, 49999),
      client
        .from("movimiento_bancario_facturas")
        .select("factura_tipo, factura_id, monto, movimiento_bancario_id")
        .range(0, 49999),
      client.from("facturas_externas").select("id").eq("pais", pais).not("movimiento_bancario_id", "is", null),
      // Universo de emitidas del país: getCobroReal se evalúa sobre CADA una. Incluye las de
      // factoring liquidada aunque no tengan MBF ni FK — es justo la mejora que las excluye de
      // "Por cobrar". 126 filas en Chile hoy → resolución en bloque, sin N+1.
      client.from("facturas_sii_emitidas").select("id, monto_total, estado_factoring").eq("pais", pais).range(0, 49999),
      // Insumos de la vía factoring del resolver (fecha de liquidación). Incluye el link escalar
      // `factura_sii_emitida_id` para datar el factoring 1:1 (Camino 2 del resolver).
      client
        .from("operaciones_factoring")
        .select("id, estado, fecha_pago_real, fecha_cobro_final, fecha_operacion, factura_sii_emitida_id")
        .range(0, 49999),
      client
        .from("operaciones_factoring_facturas")
        .select("operacion_factoring_id, factura_sii_emitida_id")
        .range(0, 49999),
    ]);
  if (movsRes.error) throw movsRes.error;
  if (cobrosRes.error) throw cobrosRes.error;
  if (pagosRes.error) throw pagosRes.error;
  if (mbfRes.error) throw mbfRes.error;
  if (externasPagadasRes.error) throw externasPagadasRes.error;
  if (facEmitidasRes.error) throw facEmitidasRes.error;
  if (opFactRes.error) throw opFactRes.error;
  if (opFactFacRes.error) throw opFactFacRes.error;

  const externas = new Set((externasPagadasRes.data || []).map((r) => r.id));

  // Acumulados Σ mbf.monto por factura (moneda cruda). Se devuelven para que los consumidores
  // (VincularMovimientoModal) muestren el saldo restante de un cobro/pago parcial. Alimentan además
  // la Fuente 3 de recibidas/boletas. (Suma en moneda cruda: para splits cross-currency el monto en
  // otra moneda no se convierte acá → dirección segura; el cuadre cambiario fino lo hace el ledger.)
  const montosAcumRecibidas = new Map(); // factura_id → Σ mbf.monto
  const montosAcumEmitidas = new Map();
  const montosAcumBoletas = new Map(); // boleta_honorario_id → Σ mbf.monto (F2b)
  for (const r of mbfRes.data || []) {
    if (r.factura_tipo === "sii_recibida") {
      montosAcumRecibidas.set(r.factura_id, (montosAcumRecibidas.get(r.factura_id) || 0) + Number(r.monto || 0));
    }
    if (r.factura_tipo === "sii_emitida") {
      montosAcumEmitidas.set(r.factura_id, (montosAcumEmitidas.get(r.factura_id) || 0) + Number(r.monto || 0));
    }
    if (r.factura_tipo === "boleta_honorario") {
      montosAcumBoletas.set(r.factura_id, (montosAcumBoletas.get(r.factura_id) || 0) + Number(r.monto || 0));
    }
  }

  // ── EMITIDAS — cobro resuelto por `getCobroReal` (chokepoint único, regla 10.192) ────────────
  // Reemplaza las viejas Fuentes 1/2/3 de emitidas (FK directa booleana + puente + cuotas) por el
  // resolver, que es un superset (agrega la vía factoring liquidada). `buildContextoCobro` indexa
  // todos los insumos una sola vez → getCobroReal es O(1)/O(k) por factura, sin N+1.
  const ctxCobro = buildContextoCobro({
    mbfRows: mbfRes.data || [],
    movimientosBancarios: movsRes.data || [],
    facturasEmitidas: facEmitidasRes.data || [],
    operacionesFactoring: opFactRes.data || [],
    operacionesFactoringFacturas: opFactFacRes.data || [],
    planCuotas: cobrosRes.data || [],
  });
  const emitidas = new Set();
  for (const f of facEmitidasRes.data || []) {
    if (getCobroReal(f.id, ctxCobro).cobrada) emitidas.add(f.id);
  }

  // ── RECIBIDAS / BOLETAS — pago resuelto por el puente MBF + cuotas (sin FK directa, regla 10.192) ─
  // Fetch liviano del monto_total/monto_bruto solo de las facturas/boletas con algún vínculo MBF.
  const recibidas = new Set();
  const boletas = new Set();
  const idsRecConVinculo = [...montosAcumRecibidas.keys()];
  const idsBolConVinculo = [...montosAcumBoletas.keys()];
  const montosRecibidas = new Map();
  const montosBoletas = new Map();
  if (idsRecConVinculo.length > 0) {
    const { data: facsRec, error: errRec } = await client
      .from("facturas_sii_recibidas")
      .select("id, monto_total")
      .in("id", idsRecConVinculo);
    if (errRec) throw errRec;
    for (const f of facsRec || []) montosRecibidas.set(f.id, Number(f.monto_total || 0));
  }
  if (idsBolConVinculo.length > 0) {
    // Boletas de honorarios (F2b): el "total" es su monto_bruto (exenta, sin IVA).
    const { data: bhs, error: errBol } = await client
      .from("boletas_honorarios")
      .select("id, monto_bruto")
      .in("id", idsBolConVinculo);
    if (errBol) throw errBol;
    for (const b of bhs || []) montosBoletas.set(b.id, Number(b.monto_bruto || 0));
  }

  // Fuente puente (MBF) — Σ mbf.monto ≥ monto_total (tolerancia 0.01). Las parciales quedan FUERA
  // del Set → siguen disponibles en el selector con su saldo restante.
  for (const [fid, acum] of montosAcumRecibidas) {
    const total = montosRecibidas.get(fid) || 0;
    if (total > 0 && acum >= total - 0.01) recibidas.add(fid);
  }
  for (const [bid, acum] of montosAcumBoletas) {
    const total = montosBoletas.get(bid) || 0;
    if (total > 0 && acum >= total - 0.01) boletas.add(bid);
  }

  // Fuente cuotas (plan de pagos) — recibida con todas las cuotas pagadas (sin pendientes).
  const acumular = (filas, fkField) => {
    const stats = {};
    for (const c of filas || []) {
      const fid = c[fkField];
      if (!stats[fid]) stats[fid] = { total: 0, pendientes: 0 };
      stats[fid].total++;
      if (c.estado === "pendiente") stats[fid].pendientes++;
    }
    return stats;
  };
  for (const [fid, { total, pendientes }] of Object.entries(acumular(pagosRes.data, "factura_sii_recibida_id"))) {
    if (total > 0 && pendientes === 0) recibidas.add(Number(fid));
  }

  return { emitidas, recibidas, externas, boletas, montosAcumRecibidas, montosAcumEmitidas, montosAcumBoletas };
};

export const getMovimientoBancarioById = async (id) => {
  const { data, error } = await client.from("movimientos_bancarios").select("*").eq("id", id).single();
  if (error) throw error;
  return data;
};

// ─── Importación (desde Edge Function clay-import) ────────

export const insertMovimientosBancarios = async (movimientos) => {
  if (!movimientos?.length) return { nuevos: 0, duplicados: 0 };

  const { data, error } = await client
    .from("movimientos_bancarios")
    .upsert(movimientos, { onConflict: "external_id", ignoreDuplicates: true })
    .select();

  if (error) throw error;
  const nuevos = data?.length || 0;
  return { nuevos, duplicados: movimientos.length - nuevos };
};

// ─── Matching (actualizar sugerencias) ────────────────────

export const actualizarMatch = async (id, matchData) => {
  const { data, error } = await client.from("movimientos_bancarios").update(matchData).eq("id", id).select();
  if (error) throw error;
  return data?.[0];
};

// ─── Confirmar / Ignorar ─────────────────────────────────

export const confirmarMovimiento = async (id, { movimiento_caja_id, procesado_por }) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "procesado",
      movimiento_caja_id,
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const ignorarMovimiento = async (id, procesado_por) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "ignorado",
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const confirmarMovimientosBulk = async (ids, procesado_por) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "procesado",
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .in("id", ids)
    .select();
  if (error) throw error;
  return data || [];
};

// ─── Cuentas bancarias ───────────────────────────────────

export const getCuentasBancarias = async (pais) => {
  const { data, error } = await client
    .from("cuentas_bancarias")
    .select("*, bancos(nombre)")
    .eq("pais", pais)
    .eq("habilitado", true)
    .order("moneda", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const actualizarSaldoCuenta = async (id, saldo, fecha) => {
  const { error } = await client
    .from("cuentas_bancarias")
    .update({ saldo_actual: saldo, saldo_fecha: fecha, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};

// ─── Resumen ─────────────────────────────────────────────

export const getResumenMovimientos = async (pais, fechaDesde, fechaHasta) => {
  const movimientos = await getMovimientosBancarios(pais, { fechaDesde, fechaHasta });

  const ingresos = movimientos.filter((m) => m.tipo === "ingreso");
  const egresos = movimientos.filter((m) => m.tipo === "egreso");
  const pendientes = movimientos.filter((m) => m.estado === "pendiente");

  return {
    totalIngresos: ingresos.reduce((sum, m) => sum + Number(m.monto), 0),
    totalEgresos: egresos.reduce((sum, m) => sum + Number(m.monto), 0),
    cantidadIngresos: ingresos.length,
    cantidadEgresos: egresos.length,
    pendientes: pendientes.length,
    total: movimientos.length,
  };
};
